/**
 * Lightweight accessible info tooltip (SVG info icon).
 *
 * One open bubble at a time. Hover / keyboard focus opens; Escape and
 * outside tap/click close. Touch toggles. Bubble is portaled to document.body
 * with fixed positioning so it works inside tables, panels, and modals.
 */
import { el, type ElChild } from "../utils/dom";

/** Operator-facing help copy - keep free of backend jargon. */
export const HELP = {
  watermark:
    "The point from which the next sync will continue. Future runs start here instead of reprocessing everything.",
  syncWindowDays:
    "How many calendar days of timesheet data each run pulls. Larger windows mean more work per run.",
  overlapDays:
    "Extra days re-fetched on every run so late edits in Keka are not missed after the previous sync.",
  lookbackDays:
    "How many calendar days of timesheet history to keep. Raising this re-fetches from today back that far on the next sync. Lowering this deletes older timesheet rows immediately.",
  lastModifiedOverlap:
    "Hours of buffer on last-modified queries. Catches updates that land slightly after the previous watermark.",
  skipped:
    "Usually normal. These records did not require updates or were outside the current sync scope.",
  accessGroup:
    "Access groups define what data a service account can access. A service account inherits permissions from the groups assigned to it.",
  scheduleType:
    "How often the automatic sync runs: every day, on selected weekdays, or on one day each month.",
  syncDisabled:
    "Scheduled syncs are paused. Manual runs can still be started from Sync.",
  partial:
    "The run finished, but some records failed or were incomplete. Check errors and skipped counts before treating it as healthy.",
  trigger: "Shows whether this run was started by the schedule or by an operator.",
} as const;

let idSeq = 0;
let openEntry: OpenEntry | null = null;

type OpenEntry = {
  trigger: HTMLElement;
  bubble: HTMLElement;
  cleanup: () => void;
};

/** Plain ⓘ control. Attach beside a label; content is exposed via aria-describedby. */
export function renderInfoTooltip(
  content: string | ElChild[],
  options?: { ariaLabel?: string; rich?: boolean; widthCh?: number }
): HTMLElement {
  const tipId = `info-tip-${++idSeq}`;
  const children = Array.isArray(content) ? content : [content];

  const bubble = el(
    "div",
    {
      id: tipId,
      className: options?.rich ? "info-tooltip__bubble info-tooltip__bubble--rich" : "info-tooltip__bubble",
      role: "tooltip",
      hidden: true,
    },
    children
  );
  if (options?.widthCh && options.widthCh > 0) {
    const width = `min(${options.widthCh}ch, calc(100vw - 24px))`;
    bubble.style.width = width;
    bubble.style.maxWidth = width;
  }

  const trigger = el("button", {
    type: "button",
    className: "info-tooltip__trigger",
    "aria-label": options?.ariaLabel ?? "More information",
    "aria-describedby": tipId,
    "aria-expanded": "false",
  });
  trigger.append(infoIconSvg());

  const root = el("span", { className: "info-tooltip" }, [trigger]);

  const show = () => openTooltip(trigger, bubble);
  const hideIfOwned = () => {
    if (openEntry?.trigger === trigger) closeTooltip();
  };

  trigger.addEventListener("mouseenter", show);
  trigger.addEventListener("focus", show);
  trigger.addEventListener("mouseleave", (ev: MouseEvent) => {
    const next = ev.relatedTarget as Node | null;
    if (next && bubble.contains(next)) return;
    hideIfOwned();
  });
  trigger.addEventListener("blur", () => {
    // Defer so a focus move into the bubble (rare) or click doesn't flash-close.
    window.setTimeout(() => {
      if (document.activeElement === trigger) return;
      if (openEntry?.trigger === trigger) closeTooltip();
    }, 0);
  });
  trigger.addEventListener("click", (ev: MouseEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
    if (openEntry?.trigger === trigger) closeTooltip();
    else openTooltip(trigger, bubble);
  });

  bubble.addEventListener("mouseleave", (ev: MouseEvent) => {
    const next = ev.relatedTarget as Node | null;
    if (next && (trigger === next || trigger.contains(next))) return;
    hideIfOwned();
  });

  return root;
}

/** ⓘ listing the columns granted by an access group. */
export function renderColumnsInfoTooltip(
  columns: readonly string[],
  options?: { widthCh?: number }
): HTMLElement {
  const body =
    columns.length > 0
      ? el(
          "ul",
          { className: "info-tooltip__list" },
          columns.map((column) => el("li", {}, [column]))
        )
      : el("p", { className: "info-tooltip__empty" }, ["No columns in this group."]);

  return renderInfoTooltip(
    [el("p", { className: "info-tooltip__title" }, ["Columns"]), body],
    {
      ariaLabel: "Show columns in this access group",
      rich: true,
      widthCh: options?.widthCh,
    }
  );
}

/** Longest column-name length across groups — keeps tooltip width stable while hovering. */
export function columnsTooltipWidthCh(
  groups: readonly { columns: readonly string[] }[]
): number {
  let longest = "Columns".length;
  for (const group of groups) {
    for (const column of group.columns) {
      if (column.length > longest) longest = column.length;
    }
  }
  // Padding for list padding + title; clamp so short/long extremes stay readable.
  return Math.min(Math.max(longest + 2, 16), 56);
}

/** Label text + info icon for forms, table headers, and detail rows. */
export function labelWithHelp(label: string, help: string): HTMLElement {
  return el("span", { className: "label-with-help" }, [label, renderInfoTooltip(help)]);
}

/** Inline SVG information icon (circle + i), inherits `currentColor`. */
function infoIconSvg(): SVGSVGElement {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("class", "info-tooltip__icon");
  svg.setAttribute("viewBox", "0 0 16 16");
  svg.setAttribute("width", "14");
  svg.setAttribute("height", "14");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");

  const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
  circle.setAttribute("cx", "8");
  circle.setAttribute("cy", "8");
  circle.setAttribute("r", "6.25");
  circle.setAttribute("fill", "none");
  circle.setAttribute("stroke", "currentColor");
  circle.setAttribute("stroke-width", "1.5");

  const stem = document.createElementNS("http://www.w3.org/2000/svg", "path");
  stem.setAttribute("d", "M8 7.25v4");
  stem.setAttribute("fill", "none");
  stem.setAttribute("stroke", "currentColor");
  stem.setAttribute("stroke-width", "1.5");
  stem.setAttribute("stroke-linecap", "round");

  const dot = document.createElementNS("http://www.w3.org/2000/svg", "circle");
  dot.setAttribute("cx", "8");
  dot.setAttribute("cy", "5.15");
  dot.setAttribute("r", "0.85");
  dot.setAttribute("fill", "currentColor");

  svg.append(circle, stem, dot);
  return svg;
}

function openTooltip(trigger: HTMLElement, bubble: HTMLElement): void {
  if (openEntry?.trigger === trigger) {
    positionBubble(trigger, bubble);
    return;
  }
  closeTooltip();

  document.body.append(bubble);
  bubble.hidden = false;
  trigger.setAttribute("aria-expanded", "true");
  positionBubble(trigger, bubble);

  const onKey = (ev: KeyboardEvent) => {
    if (ev.key === "Escape") {
      ev.stopPropagation();
      closeTooltip();
      trigger.focus();
    }
  };
  const onPointer = (ev: Event) => {
    const target = ev.target as Node | null;
    if (!target) return;
    if (trigger.contains(target) || bubble.contains(target)) return;
    closeTooltip();
  };
  const onScroll = () => positionBubble(trigger, bubble);
  const onResize = () => positionBubble(trigger, bubble);

  document.addEventListener("keydown", onKey, true);
  document.addEventListener("pointerdown", onPointer, true);
  window.addEventListener("scroll", onScroll, true);
  window.addEventListener("resize", onResize);

  openEntry = {
    trigger,
    bubble,
    cleanup: () => {
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("pointerdown", onPointer, true);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    },
  };
}

function closeTooltip(): void {
  if (!openEntry) return;
  const { trigger, bubble, cleanup } = openEntry;
  cleanup();
  trigger.setAttribute("aria-expanded", "false");
  bubble.hidden = true;
  bubble.remove();
  openEntry = null;
}

function positionBubble(trigger: HTMLElement, bubble: HTMLElement): void {
  const rect = trigger.getBoundingClientRect();
  const margin = 8;
  const gap = 6;

  // Measure after visible in DOM.
  bubble.style.left = "0px";
  bubble.style.top = "0px";
  const tip = bubble.getBoundingClientRect();

  let left = rect.left + rect.width / 2 - tip.width / 2;
  left = Math.max(margin, Math.min(left, window.innerWidth - tip.width - margin));

  let top = rect.bottom + gap;
  const placeAbove = top + tip.height + margin > window.innerHeight && rect.top - gap - tip.height >= margin;
  if (placeAbove) {
    top = rect.top - tip.height - gap;
    bubble.dataset.placement = "top";
  } else {
    bubble.dataset.placement = "bottom";
  }

  bubble.style.left = `${Math.round(left)}px`;
  bubble.style.top = `${Math.round(top)}px`;
}
